if (!customElements.get('cart-gift-attributes')) {
  customElements.define(
    'cart-gift-attributes',
    class CartGiftAttributes extends HTMLElement {
      connectedCallback() {
        this.checkbox = this.querySelector('.cart-gift-note__checkbox');
        this.emailInput = this.querySelector('.cart-gift-note__email');
        this.fromInput = this.querySelector('.cart-gift-note__from');
        this.nameInput = this.querySelector('.cart-gift-note__name');
        this.sendOnInput = this.querySelector('.cart-gift-note__send-on');
        this.messageInput = this.querySelector('.cart-gift-note__message');
        this.context = this.dataset.context || 'product';
        this.layout = this.dataset.layout || 'checkbox';
        this.syncTimer = null;
        this.isPanel = this.layout === 'panel';
        this.syncing = false;

        this.constrainSendOnDate();

        if (this.checkbox) {
          this.checkbox.addEventListener('change', () => this.onToggle());
          this.onToggle();
        } else if (this.isPanel) {
          this.inputFields().forEach((field) => {
            field.disabled = false;
          });
          this.updateToggleLabel(this.hasFilledFields() || this.dataset.hasGift === 'true');
          this.bindGiftToggle();
        }

        this.inputFields().forEach((field) => {
          field.addEventListener('input', () => {
            if (this.isPanel) this.updateToggleLabel(this.hasFilledFields());
            this.scheduleSync();
          });
          field.addEventListener('change', () => {
            if (field === this.sendOnInput) this.constrainSendOnDate();
            if (this.isPanel) this.updateToggleLabel(this.hasFilledFields());
            this.scheduleSync();
          });
        });

        const panelRoot = this.isPanel ? this.closest('openable-element, collapsible-content') : null;
        const clearButtons = [
          ...this.querySelectorAll('[data-gift-clear]'),
          ...(panelRoot ? [...panelRoot.querySelectorAll('[data-gift-clear]')] : [])
        ];
        clearButtons.forEach((btn) => {
          if (btn.dataset.giftClearBound === '1') return;
          btn.dataset.giftClearBound = '1';
          btn.addEventListener('click', (event) => {
            event.preventDefault();
            this.clearGift();
            panelRoot?.querySelector('[data-action="close"]:not([data-gift-clear])')?.click();
          });
        });

        if (this.isPanel && panelRoot) {
          panelRoot.querySelectorAll('[data-action="close"]:not([data-gift-clear])').forEach((btn) => {
            if (btn.dataset.giftSaveBound === '1') return;
            btn.dataset.giftSaveBound = '1';
            btn.addEventListener(
              'click',
              (event) => {
                if (this.hasFilledFields() && !this.validateRequiredFields()) {
                  event.preventDefault();
                  event.stopPropagation();
                  event.stopImmediatePropagation();
                  return;
                }
                this.updateToggleLabel(this.hasRequiredFields());
                this.syncToCart();
              },
              true
            );
          });
        }

        if (this.isPanel && !document.documentElement.dataset.cartGiftModalBound) {
          document.documentElement.dataset.cartGiftModalBound = '1';
          const modal = document.getElementById('cart-gift-note-panel');
          if (modal) {
            modal.addEventListener('openable-element:open', () => {
              document.body.classList.add('cart-gift-note-modal-open');
            });
            modal.addEventListener('openable-element:close', () => {
              document.body.classList.remove('cart-gift-note-modal-open');
            });
          }
        }

        if (this.context === 'product') {
          document.addEventListener('variant:added', () => this.syncToCart());
        }
      }

      inputFields() {
        return [this.emailInput, this.fromInput, this.nameInput, this.sendOnInput, this.messageInput].filter(Boolean);
      }

      todayLocalISO() {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }

      constrainSendOnDate() {
        if (!this.sendOnInput) return;

        const minDate = this.todayLocalISO();
        this.sendOnInput.min = minDate;

        const value = (this.sendOnInput.value || '').trim();
        if (value && value < minDate) {
          this.sendOnInput.value = '';
        }
      }

      hasFilledFields() {
        return this.inputFields().some((field) => field.value.trim() !== '');
      }

      hasRequiredFields() {
        return Boolean(
          this.emailInput?.value.trim() &&
            this.nameInput?.value.trim() &&
            this.messageInput?.value.trim()
        );
      }

      requiredFields() {
        return [this.emailInput, this.nameInput, this.messageInput].filter(Boolean);
      }

      validateRequiredFields() {
        let valid = true;
        this.requiredFields().forEach((field) => {
          if (!field.disabled && !field.checkValidity()) {
            field.reportValidity();
            valid = false;
          }
        });
        return valid;
      }

      isEnabled() {
        if (this.isPanel) return this.hasFilledFields();
        return Boolean(this.checkbox?.checked);
      }

      bindGiftToggle() {
        document.querySelectorAll('.cart-gift-note-trigger:not([data-gift-toggle-bound])').forEach((toggle) => {
          toggle.dataset.giftToggleBound = '1';

          toggle.addEventListener(
            'click',
            (event) => {
              const hasGift =
                toggle.dataset.hasGift === 'true' ||
                toggle.classList.contains('is-checked') ||
                toggle.getAttribute('aria-pressed') === 'true';
              const clickedBox = event.target.closest('.cart-gift-note-trigger__box');

              // Already gift: click the square to uncheck/clear in one step
              if (hasGift && clickedBox) {
                event.preventDefault();
                event.stopPropagation();
                event.stopImmediatePropagation();
                this.clearGift();
              }
              // Otherwise let toggle-button open the gift note panel
            },
            true
          );
        });
      }

      clearGift() {
        clearTimeout(this.syncTimer);
        this.inputFields().forEach((field) => {
          field.value = '';
          if (this.checkbox) field.disabled = true;
        });
        if (this.checkbox) this.checkbox.checked = false;
        this.dataset.hasGift = 'false';
        this.updateToggleLabel(false);
        this.syncToCart();
      }

      onToggle() {
        const enabled = Boolean(this.checkbox?.checked);
        this.inputFields().forEach((field) => {
          field.disabled = !enabled;
        });

        if (!enabled) {
          clearTimeout(this.syncTimer);
          this.inputFields().forEach((field) => {
            field.value = '';
          });
          this.dataset.hasGift = 'false';
          this.updateToggleLabel(false);
          this.syncToCart();
        }
      }

      scheduleSync() {
        clearTimeout(this.syncTimer);
        this.syncTimer = setTimeout(() => this.syncToCart(), 400);
      }

      getGiftAttributeValues() {
        const email = this.emailInput?.value.trim() || '';
        const from = this.fromInput?.value.trim() || '';
        const name = this.nameInput?.value.trim() || '';
        const sendOn = this.sendOnInput?.value.trim() || '';
        const message = this.messageInput?.value.trim() || '';
        const wantsGift = this.isEnabled();
        const filled = wantsGift && Boolean(email && name && message);

        return {
          filled,
          attributes: {
            gift_order: filled ? 'Yes' : '',
            gift_recipient_email: filled ? email : '',
            gift_from: filled ? from : '',
            gift_recipient_name: filled ? name : '',
            gift_send_on: filled ? sendOn : '',
            gift_message: filled ? message : ''
          }
        };
      }

      stripGiftFromNote(note) {
        if (!note || !note.includes('🎁 Gift order')) return note || '';
        return note
          .split('\n')
          .filter((line) => {
            const t = line.trim();
            if (!t) return false;
            if (t.startsWith('🎁 Gift order')) return false;
            if (t.startsWith('Recipient email:')) return false;
            if (t.startsWith('From:')) return false;
            if (t.startsWith('Recipient name:')) return false;
            if (t.startsWith('Send on:')) return false;
            if (t.startsWith('Message:')) return false;
            return true;
          })
          .join('\n')
          .trim();
      }

      updateToggleLabel(hasGift) {
        const filled = Boolean(hasGift);

        document.querySelectorAll('.cart-gift-note-trigger').forEach((toggle) => {
          toggle.dataset.hasGift = filled ? 'true' : 'false';
          toggle.setAttribute('aria-pressed', filled ? 'true' : 'false');
          toggle.classList.toggle('is-checked', filled);
          const checkmark = toggle.querySelector('.cart-gift-note-trigger__check');
          if (checkmark) checkmark.hidden = !filled;
        });
      }

      hasGiftFromCart(cart) {
        const attrs = (cart && cart.attributes) || {};
        if (
          attrs.gift_order === 'Yes' ||
          attrs.gift_recipient_email ||
          attrs.gift_from ||
          attrs.gift_recipient_name ||
          attrs.gift_send_on ||
          attrs.gift_message
        ) {
          return true;
        }
        const note = (cart && cart.note) || '';
        return (
          note.includes('🎁 Gift order') &&
          (note.includes('Recipient email:') ||
            note.includes('From:') ||
            note.includes('Recipient name:') ||
            note.includes('Send on:') ||
            note.includes('Message:'))
        );
      }

      async syncToCart() {
        if (this.syncing) return;
        if (!window.themeVariables?.routes?.cartUrl) return;

        this.syncing = true;
        try {
          const { filled, attributes: giftAttributes } = this.getGiftAttributeValues();
          this.dataset.hasGift = filled ? 'true' : 'false';
          this.updateToggleLabel(filled);

          let currentAttributes = {};
          let currentNote = '';
          try {
            const cartRes = await fetch(`${window.themeVariables.routes.cartUrl}.js`, {
              credentials: 'same-origin'
            });
            if (cartRes.ok) {
              const currentCart = await cartRes.json();
              currentAttributes = { ...(currentCart.attributes || {}) };
              currentNote = currentCart.note || '';
            }
          } catch (e) {
            // continue with gift attributes only
          }

          const mergedAttributes = {
            ...currentAttributes,
            ...giftAttributes
          };

          const payload = {
            attributes: mergedAttributes,
            note: this.stripGiftFromNote(currentNote)
          };

          const response = await fetch(`${window.themeVariables.routes.cartUrl}/update.js`, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });

          if (!response.ok) return;

          const cartContent = await response.json();
          this.updateToggleLabel(this.hasGiftFromCart(cartContent) || filled);

          document.documentElement.dispatchEvent(
            new CustomEvent('cart:updated', {
              bubbles: true,
              detail: { cart: cartContent }
            })
          );

          const cartNoteEl = document.querySelector('[is="cart-note"]');
          if (cartNoteEl && cartNoteEl.value !== payload.note) {
            cartNoteEl.value = payload.note;
          }
        } finally {
          this.syncing = false;
        }
      }
    }
  );
}
